import styled, { css } from 'styled-components';
import Paper from 'components/Paper/paper';
import {
  CampaignActionLinksProps,
  CampaignFieldStateProps,
} from 'models/campaign-mkt';

export const StyledContainer = styled.div`
  width: 100%;
  max-width: 860px;
  color: #33373c;
  font-family: 'Roboto Flex', sans-serif;
  padding: 24px 0 48px;

  @media (max-width: 768px) {
    padding-top: 18px;
  }
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 48px;
  min-height: 42px;
  margin: 0 28px 20px;

  @media (max-width: 600px) {
    gap: 24px;
    margin-inline: 0;
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  border: 0;
  padding: 8px 0;
  background: transparent;
  color: #0c50d3;
  font: inherit;
  font-size: 16px;
  cursor: pointer;

  svg {
    width: 16px;
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: #33373c;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: 0;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

export const PaperStyled = styled(Paper)`
  min-height: 976px;
  border-radius: 16px;
  padding: 40px 40px 54px;
  background-color: #fbfbfb;

  @media (max-width: 768px) {
    min-height: 0;
    padding: 30px 20px;
  }
`;

export const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const FieldGroup = styled.div`
  min-width: 0;
`;

export const FieldLabel = styled.label`
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 16px;
  max-width: calc(100% - 76px);
  padding: 0 3px;
  overflow: hidden;
  color: #6a6f77;
  background: #fbfbfb;
  font-family: 'Roboto Flex', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
  text-overflow: ellipsis;
  transform: translateY(-50%);
  transform-origin: left center;
  transition: top 120ms ease, font-size 120ms ease, color 120ms ease;
  pointer-events: none;
`;

const floatingLabel = css`
  top: 0;
  max-width: calc(100% - 36px);
  font-size: 12px;
  line-height: 16px;
`;

export const StyledTextField = styled.div<CampaignFieldStateProps>`
  position: relative;

  ${({ $filled }) =>
    $filled &&
    css`
      ${FieldLabel} {
        ${floatingLabel}
      }
    `}

  &:focus-within ${FieldLabel} {
    ${floatingLabel}
  }

  ${({ $error }) =>
    $error &&
    css`
      ${FieldLabel} {
        color: #d20003;
      }
    `}

  input {
    width: 100%;
    height: 52px;
    padding: 14px 16px;
    border: 1px solid ${({ $error }) => ($error ? '#d20003' : '#6a6f77')};
    border-radius: 16px;
    outline: 0;
    background: #fbfbfb;
    color: ${({ $error }) => ($error ? '#d20003' : '#33373c')};
    font-family: 'Roboto Flex', sans-serif;
    font-size: 14px;
    line-height: 24px;

    &:focus {
      border-color: ${({ $error }) => ($error ? '#d20003' : '#0c50d3')};
    }
  }
`;

export const FieldError = styled.p`
  margin: 5px 16px 0;
  color: #d20003;
  font-size: 12px;
  line-height: 16px;
`;

export const ActionLinks = styled.div<CampaignActionLinksProps>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ $upload }) => ($upload ? '28px' : '10px')};
  margin-top: 8px;

  button,
  a {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-height: 20px;
    border: 0;
    padding: 0;
    background: transparent;
    color: #0c50d3 !important;
    font-family: 'Roboto Flex', sans-serif;
    font-size: 14px;
    line-height: 20px;
    text-decoration: none !important;
    text-underline-offset: 2px;
    outline: 0;
    box-shadow: none;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    &:focus,
    &:active {
      background: transparent;
      color: #0c50d3 !important;
      text-decoration: none !important;
      outline: 0;
      box-shadow: none;
    }

    &:hover {
      text-decoration: underline !important;
    }

    &:focus-visible {
      outline: 1px dotted #6a6f77;
      outline-offset: 2px;
    }

    &::selection {
      background: transparent;
    }
  }

  img {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
  }
`;

export const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 22px;
  margin-top: 94px;

  @media (max-width: 768px) {
    margin-top: 64px;
  }
`;

export const CreateButton = styled.button`
  width: 100%;
  min-height: 52px;
  border: 0;
  border-radius: 26px;
  background: #0c50d3;
  color: #fff;
  font-family: 'Roboto Flex', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: #1670fa;
  }

  &:active:not(:disabled) {
    background: #164194;
  }

  &:disabled {
    background: #e7eaef;
    color: #6a6f77;
    cursor: not-allowed;
  }
`;

export const CancelButton = styled.button`
  width: 100%;
  min-height: 52px;
  border: 0;
  border-radius: 26px;
  padding: 0 16px;
  background: transparent;
  color: #0c50d3;
  font-family: 'Roboto Flex', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #e3f2ff;
  }

  &:active {
    background: #c8e6ff;
  }
`;
